import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Music4, Clock, Trophy, Flame, Award, Lock, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useStats, rankForMastery } from "@/context/StatsContext";

function timeAgo(iso) {
  if (!iso) return "Never practiced";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function isDue(iso) {
  if (!iso) return true;
  return new Date(iso).getTime() <= Date.now();
}

export default function Library() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billing, setBilling] = useState(null);
  const nav = useNavigate();
  const { stats } = useStats() || { stats: null };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: songsData }, billingRes] = await Promise.all([
        api.get("/songs"),
        api.get("/billing/status").catch(() => ({ data: null })),
      ]);
      setSongs(songsData);
      setBilling(billingRes.data);
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dueCount = songs.filter((s) => isDue(s.next_review)).length;
  const avgMastery = songs.length ? Math.round(songs.reduce((a, s) => a + (s.mastery || 0), 0) / songs.length) : 0;

  const owned = new Set(stats?.achievements || []);
  const catalog = stats?.achievements_catalog || [];
  const xpPct = stats && stats.xp_for_next_level
    ? Math.min(100, Math.round((stats.xp_into_level / stats.xp_for_next_level) * 100))
    : 0;

  return (
    <main data-testid="library-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="overline-label">Your library</p>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight">Songs to lock in</h1>
        </div>
        <div className="flex items-center gap-2">
          {billing && billing.plan !== "pro" && (
            <Button
              data-testid="library-upgrade-btn"
              variant="outline"
              onClick={() => nav("/billing")}
              className="rounded-full h-11 px-5 border-primary/50 text-primary hover:bg-primary/10"
            >
              <Sparkles size={16} className="mr-1.5" />
              {billing.song_count}/{billing.song_limit} · Upgrade
            </Button>
          )}
          <Button
            data-testid="library-cold-call-btn"
            variant="outline"
            onClick={async () => {
              try {
                const { data } = await api.get("/cold-call");
                toast(`Cold Call: ${data.title}`, { description: "No warmup — just go." });
                nav(`/songs/${data.song_id}/practice?section=${data.section_index}`);
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            disabled={songs.length === 0}
            className="rounded-full h-11 px-5"
          >
            <Zap size={16} className="mr-1.5" /> Cold Call
          </Button>
          <Button
            data-testid="library-add-song-btn"
            onClick={() => nav("/songs/new")}
            className="rounded-full h-11 px-6"
          >
            <Plus size={18} className="mr-1.5" /> Add a song
          </Button>
        </div>
      </div>

      {/* Player card */}
      {stats && (
        <div className="mt-10 border border-border rounded-2xl bg-background p-6" data-testid="player-card">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                <Trophy size={22} />
              </div>
              <div>
                <p className="overline-label">Level</p>
                <p className="font-serif text-4xl tabular-nums leading-none" data-testid="player-level">{stats.level}</p>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground tabular-nums">{stats.xp_into_level} / {stats.xp_for_next_level} XP</span>
                <span className="overline-label">to level {stats.level + 1}</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary transition-all duration-700" style={{ width: `${xpPct}%` }} data-testid="player-xp-bar" />
              </div>
            </div>
            <div className="flex items-center gap-6 md:gap-8">
              <div className="flex items-center gap-2" data-testid="player-streak">
                <Flame size={22} className={stats.streak_days > 0 ? "text-primary" : "text-muted-foreground"} />
                <div>
                  <p className="font-serif text-2xl tabular-nums leading-none">{stats.streak_days}</p>
                  <p className="overline-label mt-1">day streak</p>
                </div>
              </div>
              <div>
                <p className="font-serif text-2xl tabular-nums leading-none">{stats.total_practices}</p>
                <p className="overline-label mt-1">takes</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="mt-8 grid grid-cols-3 border border-border rounded-2xl overflow-hidden bg-background">
        <div className="p-5 border-r border-border" data-testid="stats-total">
          <p className="overline-label">In library</p>
          <p className="mt-2 font-serif text-4xl tracking-tight">{songs.length}</p>
        </div>
        <div className="p-5 border-r border-border" data-testid="stats-due">
          <p className="overline-label">Due today</p>
          <p className="mt-2 font-serif text-4xl tracking-tight">{dueCount}</p>
        </div>
        <div className="p-5" data-testid="stats-mastery">
          <p className="overline-label">Avg. mastery</p>
          <p className="mt-2 font-serif text-4xl tracking-tight">{avgMastery}<span className="text-xl align-top">%</span></p>
        </div>
      </div>

      {/* List */}
      <div className="mt-10">
        {loading ? (
          <p className="text-muted-foreground" data-testid="library-loading">Loading your songs…</p>
        ) : songs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="library-empty">
            <Music4 size={28} className="mx-auto text-muted-foreground" />
            <p className="mt-4 font-serif text-2xl">Nothing here yet.</p>
            <p className="mt-2 text-sm text-muted-foreground">Add your first song and start locking it in.</p>
            <Button data-testid="library-empty-add-btn" onClick={() => nav("/songs/new")} className="mt-6 rounded-full">
              <Plus size={16} className="mr-1.5" /> Add a song
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border border-y border-border" data-testid="library-list">
            {songs.map((s) => {
              const rank = rankForMastery(s.mastery || 0);
              return (
                <li key={s.id} className="py-5 flex items-center justify-between gap-4 group">
                  <Link to={`/songs/${s.id}`} className="flex-1 min-w-0" data-testid={`song-row-${s.id}`}>
                    <p className="font-serif text-2xl tracking-tight truncate group-hover:text-primary transition-colors">
                      {s.title}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{s.artist}</p>
                    <span
                      className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full border ${
                        rank.tone === "primary" ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"
                      }`}
                      data-testid={`song-rank-${s.id}`}
                    >
                      {rank.label}
                    </span>
                  </Link>
                  <div className="hidden sm:flex items-center gap-6 text-sm text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {timeAgo(s.last_practiced)}</span>
                    <span className={isDue(s.next_review) ? "text-primary font-medium" : ""}>
                      {isDue(s.next_review) ? "Due now" : "Scheduled"}
                    </span>
                    <span className="font-serif text-xl tabular-nums w-14 text-right">
                      {Math.round(s.mastery || 0)}%
                    </span>
                  </div>
                  <Link to={`/songs/${s.id}/practice`}>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`song-practice-btn-${s.id}`}>
                      Practice
                    </Button>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Achievements */}
      {catalog.length > 0 && (
        <section className="mt-14" data-testid="achievements-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="overline-label">Achievements</p>
              <h2 className="mt-2 font-serif text-3xl tracking-tight">Little wins along the way</h2>
            </div>
            <span className="text-sm text-muted-foreground tabular-nums" data-testid="achievements-count">
              {owned.size} / {catalog.length}
            </span>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {catalog.map((a) => {
              const unlocked = owned.has(a.id);
              return (
                <li
                  key={a.id}
                  data-testid={`achievement-${a.id}${unlocked ? "-unlocked" : ""}`}
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                    unlocked ? "border-primary/40 bg-primary/5" : "border-border bg-background opacity-70"
                  }`}
                >
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    unlocked ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    {unlocked ? <Award size={16} /> : <Lock size={14} />}
                  </span>
                  <div className="min-w-0">
                    <p className={`font-medium text-sm ${unlocked ? "" : "text-muted-foreground"}`}>{a.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </main>
  );
}
